import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { LeadWithIntelligence } from '../api/types';
import { apiGet, apiPost, apiPatch } from '../api/client';
import { stageLabel } from '../lib/format';
import styles from './TodayPage.module.css';

const GOAL = 8;

interface TodayAction {
  type: string;
  label: string;
  urgency: 'high' | 'medium' | 'low';
  canExecuteInline: boolean;
}

function getDoneToday(): Set<string> {
  try {
    const raw = sessionStorage.getItem('today-done:v1');
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function saveDoneToday(done: Set<string>) {
  sessionStorage.setItem('today-done:v1', JSON.stringify(Array.from(done)));
}

function buildLinkedInSearch(companyName: string): string {
  if (!companyName) return '#';
  const q = `site:linkedin.com/in "${companyName}" (fundador OR gerente OR propietario OR director)`;
  return `https://www.google.com/search?q=${encodeURIComponent(q)}`;
}

function buildCIFSearch(companyName: string): string {
  if (!companyName) return '#';
  return `https://empresite.eleconomista.es/${encodeURIComponent(companyName)}.html`;
}

function safeUrl(url: string | null | undefined): string {
  return url && url.startsWith('http') ? url : '';
}

function mapToTodayAction(action: TodayAction | undefined, lead: LeadWithIntelligence): TodayAction {
  const hasEmail = !!(lead.contactEmail && lead.contactEmail.trim());
  const hasPhone = !!(lead.contactPhone && lead.contactPhone.trim());
  const hasWeb = !!(lead.websiteUrl && lead.websiteUrl.trim());
  const type = action?.type ?? '';

  switch (type) {
    case 'generate_outreach':
    case 'send_followup':
    case 'infer_email':
    case 'cold_call':
    case 'find_contact':
      return action ?? { type, label: 'Acción', urgency: 'medium', canExecuteInline: true };
    case 'move_researching':
      if (hasEmail) return { type: 'generate_outreach', label: 'Generar outreach', urgency: 'high', canExecuteInline: true };
      if (hasPhone) return { type: 'cold_call', label: 'Registrar llamada', urgency: 'medium', canExecuteInline: true };
      return { type: 'find_contact', label: 'Buscar contacto', urgency: 'medium', canExecuteInline: true };
    case 'enrich_ai':
      if (!hasEmail && hasWeb) return { type: 'infer_email', label: 'Sugerir email', urgency: 'medium', canExecuteInline: true };
      if (hasPhone) return { type: 'cold_call', label: 'Registrar llamada', urgency: 'medium', canExecuteInline: true };
      return { type: 'find_contact', label: 'Buscar contacto', urgency: 'low', canExecuteInline: true };
    case 'schedule_diagnostic':
    case 'generate_proposal':
    case 'resolve_objections':
    case 'follow_proposal':
      return { type: 'generate_outreach', label: 'Generar outreach', urgency: 'high', canExecuteInline: true };
    case 'wait':
      return { type: 'find_contact', label: 'Buscar contacto', urgency: 'low', canExecuteInline: true };
    default:
      if (!hasEmail && hasWeb) return { type: 'infer_email', label: 'Sugerir email', urgency: 'medium', canExecuteInline: true };
      if (!hasEmail && !hasPhone && !hasWeb) return { type: 'find_contact', label: 'Buscar contacto', urgency: 'low', canExecuteInline: true };
      return { type: 'find_contact', label: 'Buscar contacto', urgency: 'low', canExecuteInline: true };
  }
}

const MISSING_LABELS: Record<string, string> = {
  contactEmail: 'email',
  contactName: 'contacto',
  contactPhone: 'teléfono',
  websiteUrl: 'web',
  painPoint: 'pain point',
  commercialAngle: 'ángulo',
  sector: 'sector',
  region: 'región',
  productStar: 'producto estrella',
  contactTitle: 'cargo',
  address: 'dirección',
};

interface StatusState {
  loading: boolean;
  error: string | null;
  message: { text: string; type: 'success' | 'error' } | null;
}

interface TodayData {
  queue: LeadWithIntelligence[];
  doneToday: Set<string>;
}

interface OutreachState {
  open: boolean;
  lead: LeadWithIntelligence | null;
  subject: string;
  body: string;
  loading: boolean;
  isFollowup: boolean;
}

interface CallState {
  open: boolean;
  lead: LeadWithIntelligence | null;
}

interface TodayCardProps {
  lead: LeadWithIntelligence;
  onAction: (lead: LeadWithIntelligence, actionType: string) => void;
  onIgnore: (leadId: string) => void;
}

function TodayCard({ lead, onAction, onIgnore }: TodayCardProps) {
  const intel = lead.intelligence;
  const action = mapToTodayAction(intel?.recommendedAction, lead);
  const completeness = intel?.completeness ?? { score: 0, missing: [], priority: [] };
  const fillColor = completeness.score >= 70 ? 'var(--success)' : completeness.score >= 40 ? 'var(--warning,#f59e0b)' : 'var(--color-accent)';

  const tierTag = lead.tier ? (
    <span className={`${styles.tier} ${styles[`tier${lead.tier}`]}`}>{lead.tier}</span>
  ) : null;

  const contactParts: React.ReactNode[] = [];
  if (lead.contactEmail) {
    contactParts.push(
      <a key="email" href={`mailto:${lead.contactEmail}`}>
        &#9993; {lead.contactEmail}
      </a>
    );
  }
  if (lead.contactPhone) {
    contactParts.push(
      <a key="phone" href={`tel:${lead.contactPhone}`}>
        &#128222; {lead.contactPhone}
      </a>
    );
  }
  if (lead.websiteUrl) {
    contactParts.push(
      <a key="web" href={lead.websiteUrl} target="_blank" rel="noopener">
        &#127760; web &#8599;
      </a>
    );
  }

  const linkedInUrl = safeUrl(intel?.linkedIn?.googleUrl) || buildLinkedInSearch(lead.companyName);
  const cifUrl = safeUrl(intel?.cif?.empresiteUrl) || buildCIFSearch(lead.companyName);

  const missing = completeness.priority.map((f) => MISSING_LABELS[f] || f);

  return (
    <article className={styles.card}>
      <div className={styles.cardTop}>
        <div className={styles.cardHeadwrap}>
          <p className={styles.cardMeta}>
            {tierTag}
            <span className={styles.cardMetaSector}>
              {lead.sector || '—'}
              {lead.subsector ? ` / ${lead.subsector}` : ''}
              {lead.region ? ` · ${lead.region}` : ''}
            </span>
          </p>
          <h2 className={styles.cardCompany}>{lead.companyName}</h2>
        </div>
        <div className={styles.cardBadges}>
          <span className={`${styles.stageBadge} ${styles[`stage${lead.stage}`]}`}>
            {stageLabel(lead.stage)}
          </span>
        </div>
      </div>

      {missing.length > 0 && (
        <div className={styles.cardHint}>
          <span className={styles.cardMissing}>Falta: {missing.join(', ')}</span>
        </div>
      )}

      {contactParts.length > 0 ? (
        <div className={styles.cardContact}>{contactParts}</div>
      ) : (
        <div className={`${styles.cardContact} ${styles.cardNodata}`}>Sin datos de contacto</div>
      )}

      {lead.address && (
        <div className={styles.cardAddress}>
          <a
            href={`https://www.google.com/maps/search/${encodeURIComponent(lead.companyName + ' ' + lead.address)}`}
            target="_blank"
            rel="noopener"
          >
            &#128205; {lead.address} &#8599;
          </a>
        </div>
      )}

      {(lead.painPoint || lead.commercialAngle) && (
        <div className={styles.cardIntelWrap}>
          {lead.painPoint && (
            <div className={styles.cardIntel}>
              <strong>Pain:</strong> {lead.painPoint}
            </div>
          )}
          {lead.commercialAngle && (
            <div className={styles.cardIntel}>
              <strong>Ángulo:</strong> {lead.commercialAngle}
            </div>
          )}
        </div>
      )}

      <div className={styles.completenessBar}>
        <div className={styles.completenessBarLabel}>
          <span>Datos</span>
          <span>{completeness.score}%</span>
        </div>
        <div className={styles.completenessTrack}>
          <div
            className={styles.completenessFill}
            style={{ width: `${completeness.score}%`, background: fillColor }}
          />
        </div>
      </div>

      <div className={styles.cardFooter}>
        {action.canExecuteInline ? (
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={() => onAction(lead, action.type)}
          >
            {action.label}
          </button>
        ) : (
          <span className={styles.actionPlaceholder}>{action.label || 'Sin acción'}</span>
        )}

        {lead.contactPhone && (
          <a href={`tel:${lead.contactPhone}`} className={styles.btnCall}>
            &#128222; Llamar
          </a>
        )}

        <button
          type="button"
          className={styles.btnGhost}
          onClick={() => onIgnore(lead.id)}
        >
          Ignorar
        </button>

        <div className={styles.cardLinks}>
          <Link to={`/leads/${lead.id}`} className={styles.cardEdit} title="Editar">
            &#9998; Editar
          </Link>
          <a href={linkedInUrl} target="_blank" rel="noopener">
            LinkedIn &#8599;
          </a>
          <a href={cifUrl} target="_blank" rel="noopener">
            CIF &#8599;
          </a>
          <Link to={`/leads/${lead.id}`}>Detalle</Link>
        </div>
      </div>
    </article>
  );
}

interface OutreachModalProps {
  outreach: OutreachState;
  onClose: () => void;
  onSubjectChange: (value: string) => void;
  onBodyChange: (value: string) => void;
  onCopy: () => void;
  onMarkSent: () => void;
}

function OutreachModal({ outreach, onClose, onSubjectChange, onBodyChange, onCopy, onMarkSent }: OutreachModalProps) {
  if (!outreach.open || !outreach.lead) return null;

  return (
    <div
      className={styles.modalOverlay}
      role="button"
      tabIndex={0}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClose();
        }
      }}
    >
      <div className={styles.modalBox} style={{ maxWidth: 640 }}>
        <div className={styles.modalHeader}>
          <h3>
            {outreach.isFollowup
              ? `Follow-up para ${outreach.lead.companyName}`
              : `Outreach para ${outreach.lead.companyName}`}
          </h3>
          <button type="button" className={styles.btnClose} onClick={onClose}>
            &times;
          </button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.modalField}>
            <label htmlFor="outreach-subject">Asunto</label>
            <input
              id="outreach-subject"
              type="text"
              value={outreach.subject}
              onChange={(e) => onSubjectChange(e.target.value)}
              readOnly={outreach.loading}
            />
          </div>
          <div className={styles.modalField}>
            <label htmlFor="outreach-body">Cuerpo</label>
            <textarea
              id="outreach-body"
              rows={8}
              value={outreach.body}
              onChange={(e) => onBodyChange(e.target.value)}
              readOnly={outreach.loading}
            />
          </div>
        </div>
        <div className={styles.modalFooter}>
          <button type="button" className={styles.btnGhost} onClick={onCopy}>
            &#128203; Copiar
          </button>
          <button type="button" className={styles.btnPrimary} onClick={onMarkSent}>
            &#10003; Marcar como enviado
          </button>
        </div>
      </div>
    </div>
  );
}

interface CallModalProps {
  call: CallState;
  onClose: () => void;
  onLogCall: (result: 'interested' | 'no_answer' | 'not_interested') => void;
}

function CallModal({ call, onClose, onLogCall }: CallModalProps) {
  if (!call.open || !call.lead) return null;

  return (
    <div
      className={styles.modalOverlay}
      role="button"
      tabIndex={0}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClose();
        }
      }}
    >
      <div className={styles.modalBox} style={{ maxWidth: 400 }}>
        <div className={styles.modalHeader}>
          <h3>Registrar llamada</h3>
          <button type="button" className={styles.btnClose} onClick={onClose}>
            &times;
          </button>
        </div>
        <div className={styles.modalBody}>
          <p className={styles.callCompany}>{call.lead.companyName}</p>
          <div className={styles.callButtons}>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={() => onLogCall('interested')}
            >
              &#128077; Interesado
            </button>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => onLogCall('no_answer')}
            >
              &#128222; Sin respuesta
            </button>
            <button
              type="button"
              className={styles.btnGhost}
              onClick={() => onLogCall('not_interested')}
            >
              &#128078; No interesado
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TodayPage() {
  const [todayData, setTodayData] = useState<TodayData>({ queue: [], doneToday: getDoneToday() });
  const [status, setStatus] = useState<StatusState>({ loading: true, error: null, message: null });
  const [outreach, setOutreach] = useState<OutreachState>({
    open: false,
    lead: null,
    subject: '',
    body: '',
    loading: false,
    isFollowup: false,
  });
  const [call, setCall] = useState<CallState>({ open: false, lead: null });

  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadQueue = useCallback(async () => {
    setStatus((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await apiGet('/api/leads/today-queue') as { queue?: LeadWithIntelligence[] };
      const loaded = Array.isArray(data.queue) ? data.queue : [];
      const done = getDoneToday();
      setTodayData({ queue: loaded.filter((l) => !done.has(l.id)), doneToday: done });
    } catch (err) {
      setStatus((prev) => ({ ...prev, error: err instanceof Error ? err.message : 'Error cargando cola' }));
    } finally {
      setStatus((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    loadQueue();
    refreshTimerRef.current = setInterval(loadQueue, 120000);
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [loadQueue]);

  useEffect(() => {
    if (!status.message) return;
    const t = setTimeout(() => setStatus((prev) => ({ ...prev, message: null })), 3000);
    return () => clearTimeout(t);
  }, [status.message]);

  function markDone(leadId: string) {
    const next = new Set(getDoneToday());
    next.add(leadId);
    saveDoneToday(next);
    setTodayData((prev) => ({
      ...prev,
      doneToday: next,
      queue: prev.queue.filter((l) => l.id !== leadId),
    }));
  }

  function handleIgnore(leadId: string) {
    markDone(leadId);
  }

  function handleAction(lead: LeadWithIntelligence, actionType: string) {
    switch (actionType) {
      case 'generate_outreach':
        openOutreach(lead, false);
        break;
      case 'send_followup':
        openOutreach(lead, true);
        break;
      case 'infer_email':
        suggestEmail(lead);
        break;
      case 'cold_call':
        openCallModal(lead);
        break;
      case 'find_contact':
        openLinkedIn(lead);
        break;
      default:
        setStatus((prev) => ({ ...prev, message: { text: `Acción: ${actionType}`, type: 'success' } }));
    }
  }

  async function openOutreach(lead: LeadWithIntelligence, isFollowup: boolean) {
    setOutreach({
      open: true,
      lead,
      isFollowup,
      subject: 'Cargando...',
      body: 'Generando borrador...',
      loading: true,
    });
    try {
      const data = await apiPost(`/api/llm/generate-outreach/${lead.id}`, {}) as {
        data?: { subject?: string; body?: string };
      };
      setOutreach((prev) => ({
        ...prev,
        subject: data?.data?.subject ?? '',
        body: data?.data?.body ?? '',
      }));
    } catch {
      setOutreach((prev) => ({ ...prev, subject: 'Error generando borrador', body: '' }));
    } finally {
      setOutreach((prev) => ({ ...prev, loading: false }));
    }
  }

  function closeOutreach() {
    setOutreach((prev) => ({ ...prev, open: false, lead: null }));
  }

  async function copyOutreach() {
    try {
      await navigator.clipboard.writeText(`Asunto: ${outreach.subject}\n\n${outreach.body}`);
      setStatus((prev) => ({ ...prev, message: { text: 'Copiado al portapapeles', type: 'success' } }));
    } catch {
      setStatus((prev) => ({ ...prev, message: { text: 'No se pudo copiar', type: 'error' } }));
    }
  }

  async function markAsSent() {
    if (!outreach.lead) return;
    const followupAt = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString();
    try {
      await apiPatch(`/api/leads/${outreach.lead.id}`, {
        stage: 'outreach',
        lastContactAt: new Date().toISOString(),
        nextFollowupAt: followupAt,
      });
      setStatus((prev) => ({ ...prev, message: { text: 'Marcado como enviado. Follow-up en 5 días.', type: 'success' } }));
      closeOutreach();
      markDone(outreach.lead.id);
    } catch {
      setStatus((prev) => ({ ...prev, message: { text: 'Error al marcar como enviado', type: 'error' } }));
    }
  }

  async function suggestEmail(lead: LeadWithIntelligence) {
    try {
      const data = await apiPost('/api/leads/infer-email', { leadId: lead.id }) as {
        candidates?: string[];
      };
      const candidates = data?.candidates || [];
      if (!candidates.length) {
        setStatus((prev) => ({ ...prev, message: { text: 'No se encontraron candidatos (lead sin web)', type: 'success' } }));
        return;
      }
      const list = candidates.map((c, i) => `${i + 1}. ${c}`).join('\n');
      const chosen = prompt(`Candidatos de email:\n${list}\n\nPega el número o el email a usar:`);
      if (!chosen) return;
      const email = candidates[parseInt(chosen, 10) - 1] || chosen;
      if (!email.includes('@')) {
        setStatus((prev) => ({ ...prev, message: { text: 'Email no válido', type: 'error' } }));
        return;
      }
      await apiPatch(`/api/leads/${lead.id}`, { contactEmail: email });
      setStatus((prev) => ({ ...prev, message: { text: `Email guardado: ${email}`, type: 'success' } }));
      markDone(lead.id);
    } catch {
      setStatus((prev) => ({ ...prev, message: { text: 'No se pudo sugerir email', type: 'error' } }));
    }
  }

  function openLinkedIn(lead: LeadWithIntelligence) {
    const url = safeUrl(lead.intelligence?.linkedIn?.googleUrl) || buildLinkedInSearch(lead.companyName);
    if (url && url.startsWith('http')) {
      window.open(url, '_blank');
      setStatus((prev) => ({ ...prev, message: { text: 'Busca el contacto en LinkedIn', type: 'success' } }));
    } else {
      setStatus((prev) => ({ ...prev, message: { text: 'No hay enlace de LinkedIn disponible', type: 'error' } }));
    }
  }

  function openCallModal(lead: LeadWithIntelligence) {
    setCall({ open: true, lead });
  }

  function closeCallModal() {
    setCall((prev) => ({ ...prev, open: false, lead: null }));
  }

  async function logCall(result: 'interested' | 'no_answer' | 'not_interested') {
    if (!call.lead) return;
    const stageMap = { interested: 'researching', no_answer: 'outreach', not_interested: 'lost' };
    const responseMap = { interested: 'replied', no_answer: 'none', not_interested: 'declined' };
    try {
      await apiPatch(`/api/leads/${call.lead.id}`, {
        stage: stageMap[result],
        responseStatus: responseMap[result],
        lastContactAt: new Date().toISOString(),
      });
      setStatus((prev) => ({ ...prev, message: { text: 'Llamada registrada', type: 'success' } }));
      closeCallModal();
      markDone(call.lead.id);
    } catch {
      setStatus((prev) => ({ ...prev, message: { text: 'Error registrando llamada', type: 'error' } }));
    }
  }

  return (
    <section className={styles.shell} aria-label="Cola del día">
      <header className={styles.masthead}>
        <div className={styles.mastheadWrap}>
          <p className={styles.kicker}>Cola del día</p>
          <h1 className={styles.head}>Hoy</h1>
          <p className={styles.deck}>
            {todayData.doneToday.size} trabajados hoy / objetivo {GOAL}
          </p>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.btnRefresh}
            onClick={loadQueue}
            disabled={status.loading}
          >
            {status.loading ? 'Actualizando…' : 'Actualizar'}
          </button>
        </div>
      </header>

      {status.message && (
        <div
          className={
            status.message.type === 'success' ? styles.toastSuccess : styles.toastError
          }
        >
          {status.message.text}
        </div>
      )}

      <div className={styles.pageBody}>
        {status.loading && todayData.queue.length === 0 ? (
          <div className={styles.loadingState}>
            <span>Calculando cola de hoy…</span>
          </div>
        ) : status.error && todayData.queue.length === 0 ? (
          <div className={styles.loadingState}>{status.error}</div>
        ) : todayData.queue.length === 0 ? (
          <div className={styles.loadingState}>
            <span>&#10003; Todo al día. Sin acciones pendientes.</span>
          </div>
        ) : (
          todayData.queue.map((l) => (
            <TodayCard
              key={l.id}
              lead={l}
              onAction={handleAction}
              onIgnore={handleIgnore}
            />
          ))
        )}
      </div>

      <OutreachModal
        outreach={outreach}
        onClose={closeOutreach}
        onSubjectChange={(v) => setOutreach((prev) => ({ ...prev, subject: v }))}
        onBodyChange={(v) => setOutreach((prev) => ({ ...prev, body: v }))}
        onCopy={copyOutreach}
        onMarkSent={markAsSent}
      />

      <CallModal
        call={call}
        onClose={closeCallModal}
        onLogCall={logCall}
      />
    </section>
  );
}
